import { type ISortedArray, setSortedMapItems, sortItems } from "@/lib/SortedMap"
import type { TContact, TInteraction, TLastEvaluatedKey } from "@/schemas/contacts"
import { appStore$ } from "@/stores/app.v2.store"
import { batch, observable, syncState, when } from "@legendapp/state"

type FetchingState = "idle" | "fetching" | "first-fetching" | "error"

interface DynamoData {
  recentUserInteractions: ISortedArray<TInteraction>
  _recentUserInteractionLastKey?: TLastEvaluatedKey
  isFetchingRecentUserInteractions: FetchingState
  //////////////////
  recentAccountInteractions: ISortedArray<TInteraction>
  _recentAccountInteractionLastKey?: TLastEvaluatedKey
  isFetchingRecentAccountInteractions: FetchingState
  //////////////////
  recentUserContacts: ISortedArray<TContact>
  _recentUserContactsLastKey?: TLastEvaluatedKey
  isFetchingRecentUserContacts: FetchingState
  /////////////////
  recentAccountContacts: ISortedArray<TContact>
  _recentAccountContactsLastKey?: TLastEvaluatedKey
  isFetchingRecentAccountContacts: FetchingState
  /////////////////
  recentContactInteractions: Record<string, ISortedArray<TInteraction>>
  _recentContactInteractionsLastKey: Record<string, TLastEvaluatedKey>
  isFetchingRecentContactInteractions: Record<string, FetchingState>
  //////////////////
  liveUserContactInteractions: Record<TContact["ContactPersonId"], TInteraction>
}

const initialState: DynamoData = {
  recentUserInteractions: {
    dataMap: {},
    sortMap: {},
    sorted: [],
    sortReverseMap: {},
    sorted_keyExtractor: (item) => item.RingId!,
  },
  _recentUserInteractionLastKey: undefined,
  isFetchingRecentUserInteractions: "idle",
  /////////////////
  recentAccountInteractions: {
    dataMap: {},
    sortMap: {},
    sorted: [],
    sortReverseMap: {},
    sorted_keyExtractor: (item) => item.RingId!,
  },
  _recentAccountInteractionLastKey: undefined,
  isFetchingRecentAccountInteractions: "idle",
  //////////////////
  recentUserContacts: {
    dataMap: {},
    sortMap: {},
    sorted: [],
    sortReverseMap: {},
    sorted_keyExtractor: (item) => item.ContactPersonId!,
  },
  _recentUserContactsLastKey: undefined,
  isFetchingRecentUserContacts: "idle",
  //////////////////
  recentAccountContacts: {
    dataMap: {},
    sortMap: {},
    sorted: [],
    sortReverseMap: {},
    sorted_keyExtractor: (item) => item.ContactPersonId!,
  },
  _recentAccountContactsLastKey: undefined,
  isFetchingRecentAccountContacts: "idle",
  //////////////////
  recentContactInteractions: {},
  _recentContactInteractionsLastKey: {},
  isFetchingRecentContactInteractions: {},
  //////////////////
  liveUserContactInteractions: {},
}

interface DynamoStore extends DynamoData {
  updateRecentUserInteractions: (interactions: Array<TInteraction>, lastKey?: TLastEvaluatedKey) => Promise<void>
  clearRecentUserInteractions: () => void
  ////////////////////
  updateRecentAccountInteractions: (interactions: Array<TInteraction>, lastKey?: TLastEvaluatedKey) => Promise<void>
  clearRecentAccountInteractions: () => void
  ////////////////////
  updateRecentUserContacts: (contacts: Array<TContact>, lastKey?: TLastEvaluatedKey) => Promise<void>
  clearRecentUserContacts: () => void
  resortRecentUserContacts: () => Promise<void>
  ////////////////////
  updateRecentAccountContacts: (contacts: Array<TContact>, lastKey?: TLastEvaluatedKey) => Promise<void>
  clearRecentAccountContacts: () => void
  ////////////////////
  updateRecentContactInteractions: (contactId: string, interactions: Array<TInteraction>, lastKey?: TLastEvaluatedKey) => Promise<void>
  clearRecentContactInteractions: (contactId: string) => void
  ////////////////////
  updateLiveUserContactInteractions: (contactId: string, interaction?: TInteraction) => Promise<void>
  clearLiveUserContactInteractions: () => void
}

export const dynamoStore$ = observable<DynamoStore>({
  ...initialState,
  //// Recent User Interactions
  /**
   * @deprecated Do not use
   */
  updateRecentUserInteractions: async (interactions: Array<TInteraction>, lastKey?: TLastEvaluatedKey) => {
    const storeState$ = syncState(dynamoStore$)
    await when(storeState$.isPersistLoaded)

    await setSortedMapItems({
      sortedArray: dynamoStore$.recentUserInteractions,
      items: interactions.map((interaction) => [interaction.RingId!, interaction]),
      timestampKey: "StartTimestamp",
      idKey: "RingId",
    })

    if (lastKey) {
      dynamoStore$._recentUserInteractionLastKey.set(lastKey)
    } else {
      dynamoStore$._recentUserInteractionLastKey.delete()
    }
  },
  /**
   * @deprecated Do not use
   */
  clearRecentUserInteractions: () => {
    batch(() => {
      dynamoStore$.recentUserInteractions.dataMap.assign({})
      dynamoStore$.recentUserInteractions.sortMap.assign({})
      dynamoStore$.recentUserInteractions.assign({ sorted: [] })
      dynamoStore$._recentUserInteractionLastKey.set(undefined)
    })
  },
  //// Recent Account Interactions
  /**
   * @deprecated Do not use
   */
  updateRecentAccountInteractions: async (interactions: Array<TInteraction>, lastKey?: TLastEvaluatedKey) => {
    const storeState$ = syncState(dynamoStore$)
    await when(storeState$.isPersistLoaded)

    await setSortedMapItems({
      sortedArray: dynamoStore$.recentAccountInteractions,
      items: interactions.map((interaction) => [interaction.RingId!, interaction]),
      timestampKey: "StartTimestamp",
      idKey: "RingId",
    })

    if (lastKey) {
      dynamoStore$._recentAccountInteractionLastKey.set(lastKey)
    } else {
      dynamoStore$._recentAccountInteractionLastKey.delete()
    }
  },
  /**
   * @deprecated Do not use
   */
  clearRecentAccountInteractions: () => {
    batch(() => {
      dynamoStore$.recentAccountInteractions.dataMap.assign({})
      dynamoStore$.recentAccountInteractions.sortMap.assign({})
      dynamoStore$.recentAccountInteractions.assign({ sorted: [] })
      dynamoStore$._recentAccountInteractionLastKey.set(undefined)
    })
  },
  //// Recent User Contacts
  /**
   * @deprecated Do not use
   */
  updateRecentUserContacts: async (contacts: Array<TContact>, lastKey?: TLastEvaluatedKey) => {
    const storeState$ = syncState(dynamoStore$)
    await when(storeState$.isPersistLoaded)

    await setSortedMapItems({
      sortedArray: dynamoStore$.recentUserContacts,
      items: contacts.map((contact) => [contact.ContactPersonId!, contact]),
      timestampKey: "LastTimestamp",
      idKey: "ContactPersonId",
      forceSingleSortKeyPerItem: true,
      timestampFunction: (contact) => {
        const userPersonID = (appStore$.userData.user.id.get() ?? "").toString()
        const sortedLength = dynamoStore$.recentContactInteractions[contact.ContactPersonId].sorted.get()?.length ?? 0

        if (sortedLength === 0) {
          return -1
        }

        const interactions = dynamoStore$.recentContactInteractions[contact.ContactPersonId].get().sorted
        const lastInteraction = (() => {
          for (let i = interactions.length - 1; i >= 0; i--) {
            if (interactions[i].UserPersonId === userPersonID) {
              return interactions[i]
            }
          }
          return undefined
        })()

        if (lastInteraction?.StartTimestamp === undefined) {
          return -1
        }

        return lastInteraction.StartTimestamp as number
      },
    })

    if (lastKey) {
      dynamoStore$._recentUserContactsLastKey.set(lastKey)
    } else {
      dynamoStore$._recentUserContactsLastKey.delete()
    }
  },
  /**
   * @deprecated Do not use
   */
  resortRecentUserContacts: async () => {
    const storeState$ = syncState(dynamoStore$)
    await when(storeState$.isPersistLoaded)

    await sortItems({
      sortedArray: dynamoStore$.recentUserContacts,
    })
  },
  /**
   * @deprecated Do not use
   */
  clearRecentUserContacts: () => {
    batch(() => {
      dynamoStore$.recentUserContacts.dataMap.assign({})
      dynamoStore$.recentUserContacts.sortMap.assign({})
      dynamoStore$.recentUserContacts.assign({ sorted: [] })
      dynamoStore$._recentUserContactsLastKey.set(undefined)
    })
  },
  //// Recent Account Contacts
  /**
   * @deprecated Do not use
   */
  updateRecentAccountContacts: async (contacts: Array<TContact>, lastKey?: TLastEvaluatedKey) => {
    const storeState$ = syncState(dynamoStore$)
    await when(storeState$.isPersistLoaded)

    await setSortedMapItems({
      sortedArray: dynamoStore$.recentAccountContacts,
      items: contacts.map((contact) => [contact.ContactPersonId!, contact]),
      timestampKey: "LastTimestamp",
      idKey: "ContactPersonId",
      forceSingleSortKeyPerItem: true,
    })

    if (lastKey) {
      dynamoStore$._recentAccountContactsLastKey.set(lastKey)
    } else {
      dynamoStore$._recentAccountContactsLastKey.delete()
    }
  },
  /**
   * @deprecated Do not use
   */
  clearRecentAccountContacts: () => {
    batch(() => {
      dynamoStore$.recentAccountContacts.dataMap.assign({})
      dynamoStore$.recentAccountContacts.sortMap.assign({})
      dynamoStore$.recentAccountContacts.assign({ sorted: [] })
      dynamoStore$._recentAccountContactsLastKey.set(undefined)
    })
  },
  //// Recent Contact Interactions
  /**
   * @deprecated Do not use
   */
  updateRecentContactInteractions: async (contactId: string, interactions: Array<TInteraction>, lastKey?: TLastEvaluatedKey) => {
    const storeState$ = syncState(dynamoStore$)
    await when(storeState$.isPersistLoaded)

    await setSortedMapItems({
      sortedArray: dynamoStore$.recentContactInteractions[contactId],
      items: interactions.map((interaction) => [interaction.RingId!, interaction]),
      timestampKey: "StartTimestamp",
      idKey: "RingId",
      forceSingleSortKeyPerItem: true,
    })

    if (lastKey) {
      dynamoStore$._recentContactInteractionsLastKey[contactId].set(lastKey)
    } else {
      dynamoStore$._recentContactInteractionsLastKey[contactId].delete()
    }

    // Resort the recent user contacts, we don't need to wait for this
    dynamoStore$.resortRecentUserContacts()
  },
  /**
   * @deprecated Do not use
   */
  clearRecentContactInteractions: (contactId: string) => {
    batch(() => {
      dynamoStore$.recentContactInteractions[contactId].dataMap.assign({})
      dynamoStore$.recentContactInteractions[contactId].sortMap.assign({})
      dynamoStore$.recentContactInteractions[contactId].assign({ sorted: [] })
      dynamoStore$._recentContactInteractionsLastKey[contactId].delete()
    })
  },
  //// Live User Contact Interactions
  /**
   * @deprecated Do not use
   */
  updateLiveUserContactInteractions: async (contactId: string, interaction?: TInteraction) => {
    const storeState$ = syncState(dynamoStore$)
    await when(storeState$.isPersistLoaded)

    if (interaction) {
      dynamoStore$.liveUserContactInteractions[contactId].set(interaction)
    } else {
      dynamoStore$.liveUserContactInteractions[contactId].delete()
    }
  },
  /**
   * @deprecated Do not use
   */
  clearLiveUserContactInteractions: () => {
    dynamoStore$.liveUserContactInteractions.assign({})
  },
})
